import { Typography, Box } from "@material-ui/core";
import { FC } from "react";
import { useStyles } from "./changelog.styles";
import { changelogData } from "./data";
import {
  ChangelogEntry,
  ChangelogChange,
  EndpointChange,
  MigrationInfo,
} from "./types";

const ChangeTypeIcon: FC<{ type: ChangelogChange["type"] }> = ({ type }) => {
  const iconMap = {
    added: "+",
    changed: "~",
    deprecated: "!",
    removed: "-",
    fixed: "✓",
    security: "🔒",
  };

  return <span>{iconMap[type]}</span>;
};

const EndpointList: FC<{ endpoints: EndpointChange[] }> = ({ endpoints }) => {
  const classes = useStyles();

  return (
    <div className={classes.endpointsList}>
      <Typography
        variant="body2"
        style={{ marginBottom: "8px", fontWeight: "bold" }}
      >
        Affected Endpoints:
      </Typography>
      {endpoints.map((endpoint, index) => (
        <div key={index} className={classes.endpoint}>
          <span className={`${classes.httpMethod} ${endpoint.method}`}>
            {endpoint.method}
          </span>
          <code className={classes.endpointPath}>{endpoint.path}</code>
          {endpoint.description && (
            <span
              style={{
                marginLeft: "12px",
                color: "#666",
                fontSize: "0.9rem",
              }}
            >
              {endpoint.description}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const MigrationGuide: FC<{ migration: MigrationInfo }> = ({ migration }) => {
  const classes = useStyles();

  return (
    <div className={classes.migrationSection}>
      <div className={classes.migrationTitle}>Migration Required</div>
      <Typography variant="body2" style={{ marginBottom: "12px" }}>
        <strong>From:</strong> {migration.from}
        <br />
        <strong>To:</strong> {migration.to}
      </Typography>
      <Typography
        variant="body2"
        style={{ marginBottom: "8px", fontWeight: "bold" }}
      >
        Migration Steps:
      </Typography>
      <ol className={classes.migrationSteps}>
        {migration.steps.map((step, index) => (
          <li key={index}>{step}</li>
        ))}
      </ol>

      {migration.example && (
        <div className={classes.codeExample}>
          <div>
            <div className={classes.codeHeader}>Before (V3)</div>
            <pre className={classes.codeBlock}>{migration.example.before}</pre>
          </div>
          <div>
            <div className={classes.codeHeader}>After (V4)</div>
            <pre className={classes.codeBlock}>{migration.example.after}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

const ChangeItem: FC<{ change: ChangelogChange }> = ({ change }) => {
  const classes = useStyles();

  return (
    <div className={classes.changeSection}>
      <div className={classes.changeTypeHeader}>
        <div className={`${classes.changeTypeIcon} ${change.type}`}>
          <ChangeTypeIcon type={change.type} />
        </div>
        <div className={classes.changeTypeTitle}>{change.type}</div>
        <div className={classes.changeCategory}>{change.category}</div>
      </div>

      <div className={classes.changeDescription}>{change.description}</div>

      {change.details && (
        <ul className={classes.changeDetails}>
          {change.details.map((detail, index) => (
            <li key={index}>{detail}</li>
          ))}
        </ul>
      )}

      {change.endpoints && <EndpointList endpoints={change.endpoints} />}

      {change.migration && <MigrationGuide migration={change.migration} />}
    </div>
  );
};

const VersionCard: FC<{ entry: ChangelogEntry }> = ({ entry }) => {
  const classes = useStyles();

  return (
    <div className={classes.versionCard}>
      <div className={classes.versionHeader}>
        <div>
          <span className={classes.versionNumber}>v{entry.version}</span>
          <span className={`${classes.versionBadge} ${entry.type}`}>
            {entry.type}
          </span>
        </div>
        <span className={classes.versionDate}>
          {new Date(entry.date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </span>
      </div>

      <Typography className={classes.versionTitle}>{entry.title}</Typography>
      <Typography className={classes.versionDescription}>
        {entry.description}
      </Typography>

      {entry.changes.map((change, index) => (
        <ChangeItem key={index} change={change} />
      ))}
    </div>
  );
};

export const ChangeLog: FC = () => {
  const classes = useStyles();

  return (
    <div className={classes.changelogContainer}>
      <div className={classes.changelogHeader}>
        <Typography className={classes.changelogTitle}>
          Scoreboard API Changelog
        </Typography>
        <Typography className={classes.changelogSubtitle}>
          Track changes, updates, and migration guides for the Scoreboard API
        </Typography>
      </div>

      {changelogData.map((entry, index) => (
        <VersionCard key={index} entry={entry} />
      ))}

      <Box
        mt={4}
        p={2}
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "6px",
          paddingBottom: "24px",
        }}
      >
        <Typography variant="body2" color="textSecondary">
          <strong>Note:</strong> For detailed API documentation and interactive
          testing, please refer to the Swagger/OpenAPI specification above.
        </Typography>
      </Box>
      <Box mt={1} p={2}></Box>
    </div>
  );
};
